/**
 * TrackingDashboard — composition root for the tracking UI.
 *
 * Composes EvolutionChart + AdherenceCard + ActivePlanView + ProposalCard.
 * Each section has its own loading/error/empty state — no section blocks another.
 *
 * Security (CLAUDE.md §4): delegates to leaf components, all XSS-safe.
 *
 * Accessibility (CLAUDE.md §7, spec tracking-dashboard):
 *   - <main aria-label="..."> root landmark
 *   - <h1> page title
 *   - Each section has its own <h2> via its component
 *   - No section renders in indeterminate state
 */

import type { EvolutionPoint, MealPlan, ReplanProposal } from "@autodieta/shared";
import type { AdherenceResult } from "./AdherenceCard.js";
import { EvolutionChart } from "./EvolutionChart.js";
import { AdherenceCard } from "./AdherenceCard.js";
import { ActivePlanView } from "./ActivePlanView.js";
import { ProposalCard } from "./ProposalCard.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TrackingDashboardProps {
  // Evolution section
  evolutionPoints?: EvolutionPoint[];
  evolutionLoading?: boolean;
  evolutionError?: string;

  // Adherence section
  adherenceResult?: AdherenceResult;
  adherenceLoading?: boolean;
  adherenceError?: string;

  // Active plan section
  activePlan?: MealPlan;
  planLoading?: boolean;
  planError?: string;

  // Proposal (optional — only shown when present with status="proposed")
  proposal?: ReplanProposal;

  // Actions
  onConfirmProposal: (proposalId: string) => Promise<void> | void;
  onRejectProposal: (proposalId: string) => Promise<void> | void;

  // PDF download (plan-pdf-export — optional, enables PDF button in ActivePlanView)
  workerUrl?: string;
  getIdToken?: () => Promise<string | null>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TrackingDashboard({
  evolutionPoints,
  evolutionLoading,
  evolutionError,
  adherenceResult,
  adherenceLoading,
  adherenceError,
  activePlan,
  planLoading,
  planError,
  proposal,
  onConfirmProposal,
  onRejectProposal,
  workerUrl,
  getIdToken,
}: TrackingDashboardProps): JSX.Element {
  // Only show proposal card when status is "proposed"
  const pendingProposal =
    proposal?.status === "proposed" ? proposal : undefined;

  return (
    <main
      aria-label="Panel de seguimiento nutricional"
      className="flex flex-col gap-8"
    >
      <h1 className="text-xl font-bold tracking-tight text-zinc-100">
        Seguimiento
      </h1>

      {/* Pending proposal — shown prominently when present */}
      {pendingProposal ? (
        <ProposalCard
          proposal={pendingProposal}
          onConfirm={onConfirmProposal}
          onReject={onRejectProposal}
        />
      ) : null}

      {/* Adherence section */}
      {adherenceLoading === true ? (
        <AdherenceCard loading />
      ) : adherenceError !== undefined ? (
        <AdherenceCard error={adherenceError} />
      ) : adherenceResult !== undefined ? (
        <AdherenceCard result={adherenceResult} />
      ) : (
        <AdherenceCard />
      )}

      {/* Active plan section — PDF download enabled when workerUrl + getIdToken are present */}
      {planLoading === true ? (
        <ActivePlanView loading />
      ) : planError !== undefined ? (
        <ActivePlanView error={planError} />
      ) : activePlan !== undefined ? (
        <ActivePlanView
          plan={activePlan}
          {...(workerUrl !== undefined ? { workerUrl } : {})}
          {...(getIdToken !== undefined ? { getIdToken } : {})}
        />
      ) : (
        <ActivePlanView />
      )}

      {/* Evolution chart section */}
      {evolutionLoading === true ? (
        <EvolutionChart loading />
      ) : evolutionError !== undefined ? (
        <EvolutionChart error={evolutionError} />
      ) : evolutionPoints !== undefined ? (
        <EvolutionChart points={evolutionPoints} />
      ) : (
        <EvolutionChart />
      )}
    </main>
  );
}
